import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { inventoryIngestedText } from "@/lib/i18n/1280_b34e";   // Batch 34 · Z-1
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const REQUIRED = [
  "Transaction Type", "Transaction ID", "Transaction Date",
  "SKU ID", "Qty",
];
const OPTIONAL = [
  "SKU Name", "Unit Cost", "Amount", "Location", "Reference",
];

const POLL_MS = 2000;

export default function InventoryUploadDialog({ engagementId, onClose, onIngested }) {
  const { t, lang } = useI18n();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async () => {
    if (!file) { toast.error(t("digitalAudit.pickFile")); return; }
    setBusy(true); setMsg(t("digitalAudit.uploading"));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/digital-audit/${engagementId}/inventory/upload`,
        fd, { headers: { "Content-Type": "multipart/form-data" }});
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job_id in response");
      let elapsed = 0;
      const MAX = 15 * 60 * 1000;
      while (elapsed < MAX) {
        await new Promise((res) => setTimeout(res, POLL_MS));
        elapsed += POLL_MS;
        const jr = await api.get(`/jobs/${jobId}`);
        setMsg(jr.data?.message || "Working…");
        if (jr.data?.status === "done") {
          const res = jr.data?.result || {};
          // Batch 34 · Z-1: the refused types named with their count (EN + AR), not only "(dropped n)"
          const txt = inventoryIngestedText(res, t);
          if (Object.keys(res.dropped_type_counts || {}).length) toast.warning(txt, { duration: 15000 });
          else toast.success(txt);
          onIngested?.(res);
          onClose?.();
          return;
        }
        if (jr.data?.status === "failed") {
          throw new Error(jobErrorText(jr.data, lang, t, "Upload job failed"));
        }
      }
      throw new Error("Upload job timed out");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(false); setMsg("");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="inventory-upload-dialog">
        <DialogHeader>
          <DialogTitle>{t("digitalAudit.inventory.title")}</DialogTitle>
          <DialogDescription>
            {t("digitalAudit.inventory.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <div className="mb-1 font-medium text-foreground">{t("digitalAudit.requiredCols")}</div>
            <ul className="list-disc ps-5 space-y-0.5">
              {REQUIRED.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <div className="mt-2 mb-1 font-medium text-foreground">{t("digitalAudit.optionalCols")}</div>
            <ul className="list-disc ps-5 space-y-0.5">
              {OPTIONAL.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <div className="mt-2 text-[11px]">
              {t("digitalAudit.inventory.txnAccepts")}:{" "}
              <code>receipt</code>, <code>issue</code>, <code>adjustment</code>{" "}
              {t("digitalAudit.inventory.txnAliases")}{" "}
              <code>GRN</code>, <code>dispatch</code>, <code>variance</code>{" "}
              {t("digitalAudit.inventory.txnAlsoAccepted")}{" "}
              {t("digitalAudit.inventory.txnQtyNote")}
              <div className="mt-1" data-testid="inventory-returns-note">{t("b34e.inv.returnsNote")}</div>
            </div>
          </div>

          <input
            type="file"
            accept=".csv,.xls,.xlsx,.xlsm,.xlsb"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={busy}
            className="block w-full text-sm"
            data-testid="inventory-upload-file"
          />

          {msg && (
            <div className="rounded-md border bg-muted/40 p-2 text-xs">
              {busy && <Loader2 className="h-3 w-3 inline me-1 animate-spin" />}
              {msg}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
                     data-testid="inventory-upload-cancel">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={busy || !file}
                     data-testid="inventory-upload-submit">
              {busy ? <Loader2 className="h-3 w-3 me-1 animate-spin" /> : <Upload className="h-3 w-3 me-1" />}
              {busy ? t("digitalAudit.uploading") : t("digitalAudit.upload")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
